import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";

import Toast from "src/components/toast";
import InfiniteScroll from "src/components/infiniteScroll";
import { SearchBar } from "src/components/input";
import { PlaceholderRewardCatalogCard, RewardCatalogCard } from "./rewardCard";
import { RewardRequestsSlider, RewardRequestRow } from "./rewardRequestSlider";
import {
  fetchGamificationBalance,
  fetchGamificationRedemptionsForUser,
  fetchRewardsCatalogue,
  invalidateGamificationRewardRedemption,
} from "src/redux/actions/coaching/gamification";

import {
  GamificationRedemption,
  GamificationReward,
} from "src/types/coaching/gamification";

import _ from "lodash";
import cx from "classnames";
import styles from "./rewardsExplorer.module.scss";
import Actions from "src/components/actionbar";

const DEFAULT_THUMB = "/images/defaultIcons/default_asset.jpg";


interface RewardsExplorerProps {
  // eslint-disable-next-line no-unused-vars
  onClose: () => void;
}

export const RewardsExplorer: React.FC<RewardsExplorerProps> = ({ onClose }) => {
  const dispatch = useDispatch();

  const [rewardSearch, setRewardSearch] = useState<string | null | undefined>(undefined);
  const [requestsSliderOpen, setRequestsSliderOpen] = useState(false);

  const redeemSessionRewardIdRef = useRef<string | null>(null);
  const redeemSessionRewardNameRef = useRef<string>("Reward");
  const redeemToastShownForRef = useRef<string | null>(null);

  const { balance, rewards, loadingRewards, hasMoreCatalog, nextCatalogPage, redeeming, redeemLoaded, redeemError, lastRewardId, pendingCount, redemptionItems, loadingRedemptions, hasMoreRedemptions, nextRedemptionPage } = useSelector(
    (state: any) => {
      const gamification = _.get(state, "coaching.gamification", {});
  
      // rewards catalog pagination
      const rewardsState = _.get(gamification, "rewards", {});
      const catalogPagination = _.get(rewardsState, "pagination", {});
      const catalogTotalPages = _.get(catalogPagination, "totalPages", 0) as number;
      const catalogCurrentPage = _.get(catalogPagination, "currentPage", 0) as number;
      let nextCatalogPage: number | null = catalogCurrentPage + 1;
      if (nextCatalogPage >= catalogTotalPages) nextCatalogPage = null;
  
      // learner redemptions
      const redemptionsState = _.get(gamification, "userRedemptions", {});
      const items = (_.get(redemptionsState, "items", []) || []) as GamificationRedemption[];
      const redemptionsPagination = _.get(redemptionsState, "pagination", {});
      const redemptionsTotalPages = _.get(redemptionsPagination, "totalPages", 0) as number;
      const redemptionsCurrentPage = _.get(redemptionsPagination, "currentPage", 0) as number;
      let nextRedemptionPage: number | null = redemptionsCurrentPage + 1;
      if (nextRedemptionPage >= redemptionsTotalPages) nextRedemptionPage = null;
  
      return {
        balance: Number(_.get(gamification, "balance.item", 0)),
        rewards: (_.get(rewardsState, "items", []) || []) as GamificationReward[],
        loadingRewards: _.get(rewardsState, "loading", false) as boolean,
        hasMoreCatalog: _.get(catalogPagination, "hasMoreItems", false) as boolean,
        nextCatalogPage,
        redeeming: _.get(gamification, "redeem.loading", false) as boolean,
        redeemLoaded: _.get(gamification, "redeem.loaded", false) as boolean,
        redeemError: _.get(gamification, "redeem.error", null),
        lastRewardId: (_.get(gamification, "redeem.lastRewardId", null) || null) as string | null,
        pendingCount: _.filter(items, (r) => r.status === "PENDING").length,
        redemptionItems: items,
        loadingRedemptions: _.get(redemptionsState, "loading", false) as boolean,
        hasMoreRedemptions: _.get(redemptionsPagination, "hasMoreItems", false) as boolean,
        nextRedemptionPage,
      };
    },
    _.isEqual,
  );
  
  const fetchRewardsCatalogPage = (page: number) => {
    const request = {
      page: page,
      limit: 20,
      query: rewardSearch || undefined,
    };
  
    dispatch(fetchRewardsCatalogue(request));
  };
  
  useEffect(() => {
    fetchRewardsCatalogPage(0);
  }, []);
  
  useEffect(() => {
    if (rewardSearch === undefined) return;
    fetchRewardsCatalogPage(0);
  }, [rewardSearch]);

  useEffect(() => {
    dispatch(fetchGamificationBalance());
    dispatch(fetchGamificationRedemptionsForUser({ page: 0, limit: 50 }));
  }, []);

  const handleLoadMoreRewards = () => {
    if (nextCatalogPage !== null) {
      fetchRewardsCatalogPage(nextCatalogPage);
    }
  };

  const rewardById = useMemo(() => _.keyBy(rewards, "id"), [rewards]);

  useEffect(() => {
    if (redeeming) return;
    const rid = redeemSessionRewardIdRef.current;
    if (!rid) return;
    if (redeemError) {
      redeemSessionRewardIdRef.current = null;
      return;
    }
    if (!redeemLoaded) return;
    if (redeemToastShownForRef.current === rid) return;
    redeemToastShownForRef.current = rid;
    redeemSessionRewardIdRef.current = null;
    const name = redeemSessionRewardNameRef.current || "Reward";
    toast(
      <Toast.Sucess
        message={`Reward "${name}" has been requested successfully.`}
      />,
      { autoClose: 3000 },
    );
    redeemToastShownForRef.current = null;
  }, [redeeming, redeemLoaded, redeemError]);

  const rewardRequestRows: RewardRequestRow[] = useMemo(() => {
    const pending = _.filter(
      redemptionItems,
      (r) => r.status === "PENDING",
    ) as GamificationRedemption[];
    const sorted = [...pending].sort((a, b) => {
      const ta = new Date(a.createdDate || 0).getTime();
      const tb = new Date(b.createdDate || 0).getTime();
      return tb - ta;
    });
    return sorted.map((row) => {
      const catalogReward = rewardById[row.rewardId]; 
      return {
        id: row.id,
        rewardName: row.rewardName || catalogReward?.name || "Reward",
        banner: row.banner ?? catalogReward?.banner ?? null,
        points: row.points ?? catalogReward?.pointsCost ?? 0,
        status: row.status || "PENDING",
        createdDate: row.createdDate,
      };
    });
  }, [redemptionItems, rewardById]);

  const sortedRewards = useMemo(() => {
    const next = [...(rewards ?? [])];
    next.sort((a, b) => {
      const ca = a.pointsCost ?? 0;
      const cb = b.pointsCost ?? 0;
      return ca - cb;
    });
    return next;
  }, [rewards]);

  const isInStock = (reward: GamificationReward): boolean => {
    if (reward.quantity == null) return true;
    
    if (reward.quantity < 0) return true;
    return reward.quantity > 0;
  };
  
  const catalogList = useMemo(
    () => sortedRewards.filter(isInStock),
    [sortedRewards],
  );

  const hasSearchQuery = _.size(_.trim(rewardSearch || "")) > 0;

  const handleRedeemFollowUp = (rewardId: string) => {
    redeemSessionRewardIdRef.current = rewardId;
    redeemSessionRewardNameRef.current =
      rewards.find((r) => r.id === rewardId)?.name ?? "Reward";
    window.setTimeout(() => {
      dispatch(fetchGamificationBalance());
      dispatch(fetchGamificationRedemptionsForUser({ page: 0, limit: 50 }));
      dispatch(invalidateGamificationRewardRedemption());
    }, 1200);
  };

  const handleLoadMoreRedemptions = () => {
    if (nextRedemptionPage !== null) {
      dispatch(fetchGamificationRedemptionsForUser({ page: nextRedemptionPage, limit: 50 }));
    }
  };

  return (
    <div className={styles.explorer}>
      <RewardRequestsSlider
        show={requestsSliderOpen}
        onClose={() => setRequestsSliderOpen(false)}
        rows={rewardRequestRows}
        loading={loadingRedemptions}
        hasMore={hasMoreRedemptions}
        onLoadMore={handleLoadMoreRedemptions}
      />
      <div className={styles.explorerTop}>
        <button type="button" className={styles.backLink} onClick={onClose}>
          <i className={cx("paperflite-left-arrow", styles.backLinkIcon)} aria-hidden />
          <span className={styles.backLinkLabel}>
            <span className={styles.backLinkText}>back to </span>
            <span className={styles.backLinkHighlight}>&quot;my space&quot;</span>
          </span>
        </button>
        <div className={styles.explorerActions}>
          <Actions.Button
            icon="paperflite-clock"
            className={styles.pendingBtn}
            title="pending requests"
            onClick={() => setRequestsSliderOpen(true)}
          />
          <Actions.Item
            className={styles.balancePill}
          >
            <i className={cx("paperflite-coins", styles.balanceCoin)} aria-hidden />
            <span>{(balance ?? 0).toLocaleString()}</span>
          </Actions.Item>
        </div>
      </div>

      <div className={styles.explorerSearch}>
        <SearchBar
          query={rewardSearch || ""}
          onSearch={setRewardSearch}
          placeholder="Search rewards..."
          className={styles.rewardSearchBar}
        />
      </div>

      <InfiniteScroll
        key={rewardSearch ?? ""}
        className={styles.explorerCatalogScroll}
        loading={loadingRewards}
        hasMore={hasMoreCatalog}
        loadMore={handleLoadMoreRewards}
        loader={() => (
          <div className={styles.catalogGrid}>
            {_.times(_.size(catalogList) === 0 ? 4 : 1, (i) => (
              <PlaceholderRewardCatalogCard key={`reward-catalog-loader-${i}`} />
            ))}
          </div>
        )}
      >
        {_.size(catalogList) === 0 && !loadingRewards && (
          <div className={styles.emptyMessage} role="status">
            <h2 className={styles.title}>
              {hasSearchQuery ? "no results found" : "no rewards available!"}
            </h2>
            <p className={styles.subText}>
              {hasSearchQuery
                ? "No rewards match your search."
                : "Currently, there are no rewards in the catalog."}
            </p>
          </div>
        )}
        {_.size(catalogList) > 0 && (
          <div className={styles.catalogGrid} aria-busy={loadingRewards}>
            {catalogList.map((reward) => (
              <RewardCatalogCard
                key={reward.id}
                reward={reward}
                balance={balance}
                redeeming={redeeming}
                redeemSubmittingThis={Boolean(redeeming && lastRewardId === reward.id)}
                hasPendingRequest={redemptionItems.some(
                  (r) => r.rewardId === reward.id && r.status === "PENDING",
                )}
                onRedeem={handleRedeemFollowUp}
              />
            ))}
          </div>
        )}
      </InfiniteScroll>
    </div>
  );
};
